type TelemetryValue = string | number | boolean | null | undefined;
type TelemetryPayload = Record<string, TelemetryValue>;

declare global {
  interface Window {
    gtag?: (command: "event", eventName: string, payload?: TelemetryPayload) => void;
    sa_event?: (eventName: string, payload?: TelemetryPayload) => void;
  }
}

const ALLOWED_PAYLOAD_KEYS = new Set([
  "browser_family",
  "compressed",
  "elapsed_ms",
  "failure_type",
  "format",
  "format_count",
  "remove_background",
  "result_count_bucket",
  "source_count_bucket",
  "stitch_mode",
  "stitch_type",
  "worker_type",
  "zipped",
]);

const EVENT_NAME_PATTERN = /^cc_[a-z0-9_]+$/;

export const getBrowserFamily = () => {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent;
  if (/Firefox\//.test(ua)) return "firefox";
  if (/Edg\//.test(ua)) return "edge";
  if (/Chrome\//.test(ua) || /Chromium\//.test(ua)) return "chromium";
  if (/Safari\//.test(ua)) return "safari";
  return "unknown";
};

export const toCountBucket = (count: number) => {
  if (count <= 1) return "1";
  if (count <= 5) return "2-5";
  if (count <= 20) return "6-20";
  if (count <= 50) return "21-50";
  return "51+";
};

export const classifyTelemetryError = (message?: string) => {
  const value = (message || "").toLowerCase();
  if (value.includes("timeout") || value.includes("timed out")) return "timeout";
  if (value.includes("cors")) return "cors";
  if (value.includes("fetch") || value.includes("http")) return "fetch";
  if (value.includes("unsupported")) return "unsupported";
  if (value.includes("background")) return "background_removal";
  if (value.includes("zip")) return "zip";
  if (value.includes("ffmpeg")) return "ffmpeg";
  if (value.includes("worker")) return "worker";
  if (value.includes("encode") || value.includes("conversion")) return "encode";
  return "unknown";
};

const sanitizePayload = (payload: TelemetryPayload = {}): TelemetryPayload => {
  const sanitized: TelemetryPayload = {
    browser_family: getBrowserFamily(),
  };

  Object.entries(payload).forEach(([key, value]) => {
    if (!ALLOWED_PAYLOAD_KEYS.has(key) || value === undefined || value === null) return;
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      sanitized[key] = value;
    }
  });

  return sanitized;
};

export const trackTelemetryEvent = (eventName: string, payload: TelemetryPayload = {}) => {
  if (typeof window === "undefined" || !EVENT_NAME_PATTERN.test(eventName)) return;
  const sanitizedPayload = sanitizePayload(payload);

  try {
    window.gtag?.("event", eventName, sanitizedPayload);
  } catch {
    // Analytics must never affect local image processing.
  }

  try {
    window.sa_event?.(eventName, sanitizedPayload);
  } catch {
    // Analytics must never affect local image processing.
  }
};

export type { TelemetryPayload };
